/**
 * IssueProposalReview — Displays LLM-aggregated issue proposals for user review.
 *
 * After the user clicks "Save & Create Issues", the LLM aggregates voice
 * comments into proposed GitHub issues. This component lets the user:
 *  - See category icons for each proposal type
 *  - Edit proposal titles and descriptions
 *  - Delete unwanted proposals
 *  - Confirm to create the final GitHub issues
 *  - Cancel to go back to recording mode
 */
import type { IssueProposal, FeedbackCategory } from "../../types/feedback";
import { FEEDBACK_CATEGORY_LABELS } from "../../types/feedback";

const CATEGORY_ICONS: Record<FeedbackCategory, string> = {
  bug: "🐛",
  kudos: "⭐",
  "feature-request": "💡",
  "docs-bug": "📄",
  usability: "🎯",
  performance: "⚡",
};

interface IssueProposalReviewProps {
  readonly proposals: IssueProposal[];
  readonly onUpdateProposal: (index: number, proposal: IssueProposal) => void;
  readonly onDeleteProposal: (index: number) => void;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

export function IssueProposalReview({
  proposals,
  onUpdateProposal,
  onDeleteProposal,
  onConfirm,
  onCancel,
}: IssueProposalReviewProps) {
  return (
    <div className="fb-proposal-review">
      <div className="fb-proposal-header">
        <h3 className="fb-proposal-heading">
          Review Proposed Issues ({proposals.length})
        </h3>
        <p className="fb-proposal-hint">
          Edit titles and descriptions, or delete proposals you don&rsquo;t
          need.
        </p>
      </div>

      <div className="fb-proposal-list">
        {proposals.map((proposal, index) => (
          <div key={index} className="fb-proposal-card">
            <div className="fb-proposal-card-header">
              <span
                className="fb-proposal-category-icon"
                title={FEEDBACK_CATEGORY_LABELS[proposal.category]}
                aria-label={FEEDBACK_CATEGORY_LABELS[proposal.category]}
              >
                {CATEGORY_ICONS[proposal.category]}
              </span>
              <span className="fb-proposal-index">#{index + 1}</span>
              <span className="fb-proposal-category-label">
                {FEEDBACK_CATEGORY_LABELS[proposal.category]}
              </span>
              <span className="fb-proposal-sources">
                {proposal.sourceCommentIndices.length} recording
                {proposal.sourceCommentIndices.length !== 1 ? "s" : ""}
              </span>
              <button
                type="button"
                className="fb-proposal-delete"
                onClick={() => onDeleteProposal(index)}
                aria-label={`Delete proposal ${index + 1}`}
                title="Remove this proposal"
              >
                ✕
              </button>
            </div>

            {/* Title */}
            <input
              className="fb-proposal-title-input"
              value={proposal.title}
              onChange={(e) =>
                onUpdateProposal(index, { ...proposal, title: e.target.value })
              }
              aria-label={`Proposal ${index + 1} title`}
              maxLength={200}
            />

            {/* Description */}
            <textarea
              className="fb-proposal-desc-textarea"
              value={proposal.description}
              onChange={(e) =>
                onUpdateProposal(index, {
                  ...proposal,
                  description: e.target.value,
                })
              }
              aria-label={`Proposal ${index + 1} description`}
              rows={3}
            />
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="fb-proposal-actions">
        <button
          type="button"
          className="fb-action-btn fb-action-btn--end"
          onClick={onCancel}
        >
          ← Back
        </button>
        <button
          type="button"
          className="fb-action-btn fb-action-btn--save"
          onClick={onConfirm}
          disabled={proposals.length === 0}
        >
          Submit Feedback
        </button>
      </div>
    </div>
  );
}
